/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Button } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

export interface ISearchFormProps {
    hideSearchLabel: boolean | undefined;
    isSearchFormExpanded?: boolean | undefined;
    disableSubmitSearch: boolean | undefined;
    searchTextInput: React.RefObject<HTMLInputElement>;
    maxChars: number;
    searchQsp: string;
    searchPlaceholder: string;
    query: string;
    ariaLabelCancel: string;
    ariaLabelSearch: string;
    ariaLabelSubmit: string;
    handleCancelSearchChange(): void;
    handleInputChange(query: string): void;
    handleFocused(): void;
    onSubmit(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>, eventType?: string): Promise<void>;
    isParentCustomModule?: boolean;
    iconURL?: string;
}

export interface ISearchFormViewProps {
    input: React.ReactNode;
    submitBtn: React.ReactNode;
    cancelBtn: React.ReactNode;
}

/**
 * On Input Change functionality.
 * @param handleInputChange -Input change function.
 * @returns Set updated input value.
 */
const onInputChangeFunction = (handleInputChange: (query: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(event.target.value || '');
};
export const FormComponent = (props: ISearchFormProps): ISearchFormViewProps => {
    const {
        searchQsp,
        searchPlaceholder,
        hideSearchLabel,
        searchTextInput,
        handleInputChange,
        maxChars,
        disableSubmitSearch,
        handleCancelSearchChange,
        query,
        ariaLabelCancel,
        ariaLabelSearch,
        ariaLabelSubmit,
        handleFocused,
        onSubmit,
        isParentCustomModule,
        iconURL
    } = props;

    const onInputChange = onInputChangeFunction(handleInputChange);
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const element = document.getElementById("ms-search-submit") as HTMLElement;
            element?.click();
        }
        return false;
    };

    const inputNode = !hideSearchLabel ? (
        <input
            className='ms-search__form-control'
            type='text'
            aria-label={ariaLabelSearch}
            name={searchQsp}
            placeholder={searchPlaceholder}
            ref={searchTextInput}
            onChange={onInputChange}
            maxLength={maxChars}
            value={query}
            onFocus={handleFocused}
            onKeyDown={handleKeyDown}
            data-testid='Search'
        />
    ) : (
        <input
            className='ms-search__form-control'
            type='text'
            aria-label={ariaLabelSearch}
            name={searchQsp}
            placeholder={searchPlaceholder}
            ref={searchTextInput}
            onChange={onInputChange}
            value={query}
            onFocus={handleFocused}
            onKeyDown={handleKeyDown}
            data-testid='Search'
        />
    );

    const submitBtnNode = !disableSubmitSearch &&
        (isParentCustomModule ? <Button
            className='ms-search__form-newSubmitSearch'
            id='ms-search-submit'
            aria-label={ariaLabelSubmit}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => onSubmit(e, 'search-box-icon')}
            tabIndex={-1}
        > <img src={iconURL!} alt='search icon' width={25} height={25} /> </Button>
            : <Button
                className='ms-search__form-submitSearch'
                id='ms-search-submit'
                data-testid='SearchIcon'
                aria-label={ariaLabelSubmit}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => onSubmit(e, 'search-box-icon')}
                tabIndex={-1} />);

    const cancelBtNoden = (
        <Button
            className={!disableSubmitSearch ? 'ms-search__form-cancelSearch' : 'ms-search__form-cancelSearchNoSumbit'}
            aria-label={ariaLabelCancel}
            onClick={handleCancelSearchChange}
            type='button'
            tabIndex={-1}
        />
    );

    return { input: inputNode, cancelBtn: cancelBtNoden, submitBtn: submitBtnNode };
};